import {
  FormControl,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useContext, useEffect, useState } from "react";
import DateLocalizationProvider from "../../../components/element/DateLocalizationProvider";
import SearchAircraftDialog from "../../../components/element/SearchAircraftDialog";
import SearchAirlineDialog from "../../../components/element/SearchAirlineDialog";
import SearchAirportDialog from "../../../components/element/SearchAirportDialog";
import { StorageContext } from "../../../services/StorageProvider";
import { DATE_FORMAT, TIME_FORMAT, AIRPORT_TYPES, KEYBOARD_KEYS, AIRPORT_UPDATING_TYPES, FORM_FIELD_KEYS } from "../../../utils/constants";
import { getAirportData } from "../../../utils/utils";
import "../CSS/CreateFlightPlan.css";

dayjs.extend(utc);

const CreateFlightPlanTable = ({
  flightDetails,
  airlineData,
  setFlightDetails,
  selectedAirline,
  setSelectedAirline,
  selectedOrigin,
  setSelectedOrigin,
  selectedDestination,
  setSelectedDestination,
  filteredDestinationAirports,
}) => {
  const [arrivalData, setArrivalData] = useState({});
  const [departureData, setDepartureData] = useState({});
  const [departureFromArrivalData, setDepartureFromArrivalData] = useState({});
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const { airportListData, fboUserProfileData } = useContext(StorageContext);
  const airportData = airportListData;
  const [showSearchAircraftDialog, setShowSearchAircraftDialog] =
    useState(false);
  const [showSearchAirportDialog, setShowSearchAirportDialog] = useState(false);
  const [showSearchAirlineDialog, setShowSearchAirlineDialog] = useState(false);
  const [airportUpdatingType, setAirportUpdatingType] = useState("");
  const [selectedDepartureTerminal, setSelectedDepartureTerminal] =
    useState("");
  const [selectedArrivalTerminal, setSelectedArrivalTerminal] = useState("");

  // Sync terminal state with flightDetails
  useEffect(() => {
    if (flightDetails.departureAirportTerminalId) {
      setSelectedDepartureTerminal(flightDetails.departureAirportTerminalId);
    } else {
      setSelectedDepartureTerminal("");
    }
  }, [flightDetails.departureAirportTerminalId]);

  useEffect(() => {
    if (flightDetails.arrivalAirportTerminalId) {
      setSelectedArrivalTerminal(flightDetails.arrivalAirportTerminalId);
    } else {
      setSelectedArrivalTerminal("");
    }
  }, [flightDetails.arrivalAirportTerminalId]);

  const handleInputChange = (key) => (event) => {
    setFlightDetails({
      ...flightDetails,
      [key]:
        key === FORM_FIELD_KEYS.AIRCRAFT_REGISTRATION_NUMBER
          ? event.target.value.toUpperCase()
          : event.target.value,
    });
  };
  const handleFlightNumberInputChange = (event) => {
    setFlightDetails({
      ...flightDetails,
      flightNumber: event.target.value,
      iataFlightNumber: event.target.value,
      icaoFlightNumber: event.target.value,
    });
  };

  const handleSearchAirlineChange = (airline) => {
    setSelectedAirline(airline);
    setFlightDetails({
      ...flightDetails,
      airlineId: airline.id,
    });
  };

  const handleDepartureDateChange = (newValue) => {
    setDepartureData((prev) => ({
      ...prev,
      date: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      departureTime: getDateAndTime(newValue, departureData?.time),
    });
  };

  const handleDepartureTimeChange = (newValue) => {
    setDepartureData((prev) => ({
      ...prev,
      time: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      departureTime: getDateAndTime(departureData?.date, newValue),
    });
  };

  const handleArrivalDateChange = (newValue) => {
    setArrivalData((prev) => ({
      ...prev,
      date: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      arrivalTime: getDateAndTime(newValue, arrivalData?.time),
    });
  };

  const handleArrivalTimeChange = (newValue) => {
    setArrivalData((prev) => ({
      ...prev,
      time: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      arrivalTime: getDateAndTime(arrivalData?.date, newValue),
    });
  };

  const handleAircraftOnSelect = (aircraft) => {
    setSelectedAircraft(aircraft);
    setFlightDetails({
      ...flightDetails,
      aircraftTypeId: aircraft?.id,
    });
  };

  const handleSelectAirportClick = (type) => {
    setAirportUpdatingType(type);
    setShowSearchAirportDialog(true);
  };

  // Helper function to filter terminals by FBO ID
  const getFilteredTerminals = (airport) => {
    if (!airport || !airport.terminals || !fboUserProfileData?.associations?.[0]?.fboId) {
      return [];
    }
    return airport.terminals.filter(
      (terminal) => terminal.fboId === fboUserProfileData.associations[0].fboId
    );
  };

  const handleAirportOnSelect = (airport) => {
    // Get full airport data with terminals from airportListData
    const fullAirportData = getAirportById(airport.id) || airport;
    
    if (airportUpdatingType === AIRPORT_UPDATING_TYPES.ORIGIN) {
      setSelectedOrigin(fullAirportData);
      // Filter terminals for this airport
      const filteredTerminals = getFilteredTerminals(fullAirportData);
      
      // Auto-select terminal if only one is available
      let terminalId = "";
      if (filteredTerminals.length === 1) {
        terminalId = filteredTerminals[0].id;
        setSelectedDepartureTerminal(terminalId);
      } else {
        setSelectedDepartureTerminal("");
      }

      const updatedFlightDetails = {
        ...flightDetails,
        departureAirportId: fullAirportData.id,
      };
      
      if (terminalId) {
        updatedFlightDetails.departureAirportTerminalId = terminalId;
      } else {
        const { departureAirportTerminalId, ...rest } = updatedFlightDetails;
        setFlightDetails(rest);
        return;
      }
      
      setFlightDetails(updatedFlightDetails);
    } else {
      setSelectedDestination(fullAirportData);
      // Filter terminals for this airport
      const filteredTerminals = getFilteredTerminals(fullAirportData);
      
      // Auto-select terminal if only one is available
      let terminalId = "";
      if (filteredTerminals.length === 1) {
        terminalId = filteredTerminals[0].id;
        setSelectedArrivalTerminal(terminalId);
      } else {
        setSelectedArrivalTerminal("");
      }

      const updatedFlightDetails = {
        ...flightDetails,
        arrivalAirportId: fullAirportData.id,
      };
      
      if (terminalId) {
        updatedFlightDetails.arrivalAirportTerminalId = terminalId;
      } else {
        const { arrivalAirportTerminalId, ...rest } = updatedFlightDetails;
        setFlightDetails(rest);
        return;
      }
      
      setFlightDetails(updatedFlightDetails);
    }
  };

  // Helper function to get airport data by ID (using common utils)
  const getAirportById = (airportId) => getAirportData(airportData, airportId);

  // Helper function to check if airport is DOM type
  const isDOMAirport = (airportId) => {
    const airport = getAirportById(airportId);
    return airport?.airportType === AIRPORT_TYPES.DOMESTIC;
  };

  // Handle terminal selection for departure
  const handleDepartureTerminalChange = (event) => {
    const terminalId = event.target.value;
    setSelectedDepartureTerminal(terminalId);

    // Only add terminal ID to flightDetails if a terminal is selected
    if (terminalId && terminalId !== "") {
      setFlightDetails({
        ...flightDetails,
        departureAirportTerminalId: terminalId,
      });
    } else {
      // Remove the key if no terminal is selected
      const { departureAirportTerminalId, ...rest } = flightDetails;
      setFlightDetails(rest);
    }
  };

  // Handle terminal selection for arrival
  const handleArrivalTerminalChange = (event) => {
    const terminalId = event.target.value;
    setSelectedArrivalTerminal(terminalId);

    // Only add terminal ID to flightDetails if a terminal is selected
    if (terminalId && terminalId !== "") {
      setFlightDetails({
        ...flightDetails,
        arrivalAirportTerminalId: terminalId,
      });
    } else {
      // Remove the key if no terminal is selected
      const { arrivalAirportTerminalId, ...rest } = flightDetails;
      setFlightDetails(rest);
    }
  };

  const getDateAndTime = (newDate, newTime) => {
    if (newDate && newTime) {
      const localDateTime = dayjs(newDate)
        .hour(newTime.hour())
        .minute(newTime.minute());
      const utcDateTime = localDateTime.utc();
      return utcDateTime.format("YYYY-MM-DDTHH:mm:ss[Z]");
    } else {
      return null;
    }
  };

  return (
    <DateLocalizationProvider>
      <div className="create-flight-plan-table-parent-container">
        <div className="create-flight-plan-table-container">
          <table className="create-flight-plan-table">
            <thead>
              <tr>
                <th>Aircraft Type</th>
                <th>Engine Type</th>
                <th>Engine Count</th>
                <th>MTOW</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div
                    className="create-flight-plan-select-aircraft"
                    onClick={() => setShowSearchAircraftDialog(true)}
                  >
                    {selectedAircraft ? (
                      <p>{selectedAircraft?.icaoCode}</p>
                    ) : (
                      <span className="placeholder-text">Search</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="create-flight-plan-non-editable-container">
                    {selectedAircraft?.engineType}
                  </div>
                </td>
                <td>
                  <div className="create-flight-plan-non-editable-container">
                    {selectedAircraft?.engineCount}
                  </div>
                </td>
                <td>
                  <div className="create-flight-plan-non-editable-container">
                    {selectedAircraft?.maxTakeOffWeight}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="create-flight-plan-table-container">
          <table className="create-flight-plan-table">
            <thead>
              <tr>
                <th className="width-140">Airline</th>
                <th className="width-140">Flight No.</th>
                <th className="width-140">Tail Number</th>
                <th className="width-260">Origin</th>
                <th className="width-260">Destination</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div
                    className="create-flight-plan-select-airline"
                    onClick={() => setShowSearchAirlineDialog(true)}
                  >
                    {selectedAirline?.airlineName ? (
                      <p>{selectedAirline?.airlineName}</p>
                    ) : (
                      <span className="placeholder-text">Search</span>
                    )}
                  </div>
                </td>
                <td>
                  <TextField
                    id="create-flight-plan-flight-no"
                    className="create-flight-plan-textfield"
                    inputProps={{
                      autoComplete: "off",
                      maxLength: 15,
                    }}
                    value={flightDetails.flightNo}
                    onChange={handleFlightNumberInputChange}
                    onKeyDown={(e) => {
                      // block non-numeric keys at typing stage
                      if (
                        !/[0-9]/.test(e.key) && // allow only digits
                        e.key !== KEYBOARD_KEYS.BACKSPACE &&
                        e.key !== KEYBOARD_KEYS.DELETE &&
                        e.key !== KEYBOARD_KEYS.ARROW_LEFT &&
                        e.key !== KEYBOARD_KEYS.ARROW_RIGHT &&
                        e.key !== KEYBOARD_KEYS.TAB
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                </td>
                <td>
                  <TextField
                    id="create-flight-plan-tail-no"
                    className="create-flight-plan-textfield"
                    inputProps={{
                      autoComplete: "off",
                      maxLength: 15,
                    }}
                    value={flightDetails.aircraftRegistrationNumber}
                    onChange={handleInputChange("aircraftRegistrationNumber")}
                  />
                </td>
                <td>
                  <div className="airport-terminal-container">
                    <div
                      className="create-flight-plan-select-airport"
                      onClick={() => handleSelectAirportClick("origin")}
                    >
                      {selectedOrigin?.name ? (
                        <p>{selectedOrigin?.name}</p>
                      ) : (
                        <span className="placeholder-text">Search</span>
                      )}
                    </div>
                    {selectedOrigin?.id && isDOMAirport(selectedOrigin.id) && (() => {
                        // Always get full airport data from airportListData to ensure terminals are included
                        const airport = getAirportById(selectedOrigin.id);
                        const filteredTerminals = getFilteredTerminals(airport);
                        const allTerminals = airport?.terminals || [];
                        
                        // Show dropdown if airport has terminals
                        if (allTerminals.length > 0) {
                          return (
                            <FormControl
                              size="small"
                              className="terminal-dropdown-container"
                            >
                              <Select
                                value={selectedDepartureTerminal}
                                onChange={handleDepartureTerminalChange}
                                displayEmpty
                                variant="outlined"
                                className="terminal-dropdown"
                              >
                                <MenuItem value="">
                                  <em>Select</em>
                                </MenuItem>
                                {filteredTerminals.length > 0 ? (
                                  // Show filtered terminals if available
                                  filteredTerminals.map((terminal) => (
                                    <MenuItem key={terminal.id} value={terminal.id}>
                                      {terminal.name}
                                    </MenuItem>
                                  ))
                                ) : (
                                  // Show all terminals if no filtered terminals
                                  allTerminals.map((terminal) => (
                                    <MenuItem key={terminal.id} value={terminal.id}>
                                      {terminal.name}
                                    </MenuItem>
                                  ))
                                )}
                              </Select>
                            </FormControl>
                          );
                        }
                        
                        // If no terminals, don't show anything
                        return null;
                      })()}
                  </div>
                </td>
                <td>
                  <div className="airport-terminal-container">
                    <div
                      className="create-flight-plan-select-airport"
                      onClick={() => handleSelectAirportClick("destination")}
                    >
                      {selectedDestination?.name ? (
                        <p>{selectedDestination?.name}</p>
                      ) : (
                        <span className="placeholder-text">Search</span>
                      )}
                    </div>
                    {selectedDestination?.id &&
                      isDOMAirport(selectedDestination.id) && (() => {
                        // Always get full airport data from airportListData to ensure terminals are included
                        const airport = getAirportById(selectedDestination.id);
                        const filteredTerminals = getFilteredTerminals(airport);
                        
                        // Show dropdown only if there are filtered terminals (associated with FBO)
                        if (filteredTerminals.length > 0) {
                          return (
                            <FormControl
                              size="small"
                              className="terminal-dropdown-container"
                            >
                              <Select
                                value={selectedArrivalTerminal}
                                onChange={handleArrivalTerminalChange}
                                displayEmpty
                                variant="outlined"
                                className="terminal-dropdown"
                              >
                                {filteredTerminals.length > 1 && (
                                  <MenuItem value="">
                                    <em>Select</em>
                                  </MenuItem>
                                )}
                                {filteredTerminals.map((terminal) => (
                                  <MenuItem key={terminal.id} value={terminal.id}>
                                    {terminal.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          );
                        }
                        
                        // If no filtered terminals, don't show anything
                        return null;
                      })()}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="create-flight-plan-table-container">
          <table className="create-flight-plan-table">
            <thead>
              <tr>
                <th className="width-200">Depart Date and Time</th>
                <th className="width-200">Arrival Date and Time</th>
              
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="create-flight-plan-picker-container">
                    <MobileDatePicker
                      id="create-flight-plan-departure-date"
                      className="create-flight-plan-datePicker"
                      format={DATE_FORMAT}
                      maxDate={arrivalData?.date && arrivalData.date.isBefore(dayjs()) ? arrivalData.date : dayjs()}
                      value={departureData?.date}
                      onChange={handleDepartureDateChange}
                    />
                    <MobileTimePicker
                      id="create-flight-plan-departure-time"
                      className="create-flight-plan-timePicker"
                      format={TIME_FORMAT}
                      value={departureData?.time}
                      onChange={handleDepartureTimeChange}
                    />
                  </div>
                </td>
                <td>
                  <div className="create-flight-plan-picker-container">
                    <MobileDatePicker
                      id="create-flight-plan-arrival-date"
                      className="create-flight-plan-datePicker "
                      format={DATE_FORMAT}
                      minDate={departureData?.date}
                      maxDate={dayjs()}
                      value={arrivalData?.date}
                      onChange={handleArrivalDateChange}
                    />
                    <MobileTimePicker
                      id="create-flight-plan-arrival-time"
                      className="create-flight-plan-timePicker"
                      format={TIME_FORMAT}
                      value={arrivalData?.time}
                      onChange={handleArrivalTimeChange}
                    />
                  </div>
                </td>
            
              </tr>
            </tbody>
          </table>
        </div>
        <SearchAircraftDialog
          open={showSearchAircraftDialog}
          onClose={() => setShowSearchAircraftDialog(false)}
          onSelectAircraft={handleAircraftOnSelect}
        />
        <SearchAirportDialog
          open={showSearchAirportDialog}
          onClose={() => setShowSearchAirportDialog(false)}
          airportData={
            airportUpdatingType === AIRPORT_UPDATING_TYPES.DESTINATION && filteredDestinationAirports
              ? filteredDestinationAirports
              : airportData
          }
          onSelectAirport={handleAirportOnSelect}
          disableSearch={airportUpdatingType === AIRPORT_UPDATING_TYPES.DESTINATION}
        />
        <SearchAirlineDialog
          open={showSearchAirlineDialog}
          onClose={() => setShowSearchAirlineDialog(false)}
          onSelectAirline={handleSearchAirlineChange}
        />
      </div>
    </DateLocalizationProvider>
  );
};

export default CreateFlightPlanTable;
